import { Component, OnInit } from '@angular/core';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { RuleInterface } from '@vendasta/automata';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  sleepTimeRangeEndHour,
  sleepTimeRangeStartHour,
  TimeRangeSliderComponent,
} from '../../../common/controls/time-range-slider.component';
import { sleepWeekdays, WeekdaySelectorComponent } from '../../../common/controls/weekday-selector.component';
import { nonNegativeIntegerValidator } from '../../../common/utils';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';

export type RepeatWait = '30days' | '60days' | '90days';
export type TimeFrame = 'hours' | 'days' | 'weeks' | 'months';
export type SendType = 'use-default' | 'send-now' | 'send-custom';
export type SendChannel = 'both' | 'email' | 'sms' | 'email-fallback-sms' | 'sms-fallback-email';

@Component({
  templateUrl: './send-review-request-panel.component.html',
  styleUrls: ['./send-review-request-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatRadioModule,
    MatDividerModule,
    MatCheckboxModule,
    TimeRangeSliderComponent,
    WeekdaySelectorComponent,
    GalaxyTooltipModule,
  ],
})
export class SendReviewRequestPanelComponent extends CommonActionPanelComponent implements OnInit {
  form = new FormGroup({
    sendChannel: new FormControl<SendChannel>('sms-fallback-email', Validators.required),
    sendType: new FormControl<SendType>('use-default', Validators.required),
    sendCustomForm: new FormGroup({
      number: new FormControl<number>(4, [Validators.required, nonNegativeIntegerValidator]),
      unit: new FormControl<TimeFrame>('hours'),
      timeRange: new FormGroup({}),
    }),
    sendAgain: new FormControl<boolean>(false),
    sendAgainForm: new FormGroup({
      number: new FormControl<number>(3, [Validators.required, nonNegativeIntegerValidator]),
      unit: new FormControl<TimeFrame>('hours'),
      repeat: new FormControl<number>(1, Validators.required),
    }),
    repeatWait: new FormControl<RepeatWait>('60days'),
  });

  timeRangeStartHour: number;
  timeRangeEndHour: number;
  weekdays: string[] = [];

  ngOnInit(): void {
    super.ngOnInit();

    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    this.form.controls.sendChannel.setValue(jsonData?.config?.send_channel || 'sms-fallback-email');
    this.form.controls.sendType.setValue(jsonData?.config?.send_type || 'use-default');
    if (jsonData?.config?.send_type === 'send-custom') {
      this.form.controls.sendCustomForm.controls.number.setValue(jsonData?.config?.send_custom?.number || 4);
      this.form.controls.sendCustomForm.controls.unit.setValue(jsonData?.config?.send_custom?.unit || 'hours');
    }
    if (jsonData?.config?.send_again) {
      this.form.controls.sendAgain.setValue(true);
      this.form.controls.sendAgainForm.controls.number.setValue(jsonData?.config?.send_again.number || 3);
      this.form.controls.sendAgainForm.controls.unit.setValue(jsonData?.config?.send_again.unit || 'hours');
      this.form.controls.sendAgainForm.controls.repeat.setValue(jsonData?.config?.send_again.repeat || 1);
    }
    this.form.controls.repeatWait.setValue(jsonData?.config?.repeat_wait || '60days');

    this.timeRangeStartHour = jsonData?.config?.send_custom?.time_range_start || 8;
    this.timeRangeEndHour = jsonData?.config?.send_custom?.time_range_end || 20;
    this.weekdays = jsonData?.config?.send_custom?.weekdays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

    this.savingEnabled = true;
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    jsonData.config = jsonData?.config || {};
    jsonData.config.send_channel = this.form.controls.sendChannel.value;
    jsonData.config.send_type = this.form.controls.sendType.value;
    if (this.form.controls.sendType.value === 'send-custom') {
      jsonData.config.send_custom = {
        number: this.form.controls.sendCustomForm.controls.number.value,
        unit: this.form.controls.sendCustomForm.controls.unit.value,
        time_range_start: this.form.controls.sendCustomForm.controls.timeRange.get(sleepTimeRangeStartHour)?.value,
        time_range_end: this.form.controls.sendCustomForm.controls.timeRange.get(sleepTimeRangeEndHour)?.value,
        weekdays: this.form.controls.sendCustomForm.get(sleepWeekdays)?.value,
      };
    }
    if (this.form.controls.sendAgain.value) {
      jsonData.config.send_again = {
        number: this.form.controls.sendAgainForm.controls.number.value,
        unit: this.form.controls.sendAgainForm.controls.unit.value,
        repeat: this.form.controls.sendAgainForm.controls.repeat.value,
      };
    }
    jsonData.config.repeat_wait = this.form.controls.repeatWait.value;
    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    const controlsToValidate = [];
    if (this.form.controls.sendType.value === 'send-custom') {
      controlsToValidate.push(this.form.controls.sendCustomForm);
    }
    if (this.form.controls.sendAgain.value) {
      controlsToValidate.push(this.form.controls.sendAgainForm);
    }
    return controlsToValidate;
  }

  isFormValid(): boolean {
    let isValid = true;
    if (this.form.controls.sendType.value === 'send-custom') {
      isValid = isValid && this.form.controls.sendCustomForm.valid;
    }
    if (this.form.controls.sendAgain.value) {
      isValid = isValid && this.form.controls.sendAgainForm.valid;
    }
    return isValid;
  }
}
